import { Formik } from "formik";
import { useMemo } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import { toast } from "react-toastify";
import { Container, Grid, MenuItem } from "@material-ui/core";

import { useAppDispatch, useAppSelector } from "../../../hooks";
import { history } from "../../..";
import { StockpileFormSchema } from "../schema/stockpile-form-schema";
import { STORAGE_AREA_STATUSES } from "../storage-area-status";
import PageHero from "../../shared/page-hero";
import storageAreaService from "../../../services/storageAreaService";
import { getStockpile, setStockpile } from "../../../store/storageAreaStore";
import { SetStockpileAction } from "../../../interface/redux-types/SetStockpileAction";
import { Stockpile } from "../../../interface/api/Stockpile";
import { StockpileFormValues } from "../../../interface/formik/StockpileFormValues";
import FormErrorMessage from "../../shared/form-error-message";
import FormTextField from "../../shared/form-text-field";
import FormSelect from "../../shared/form-select";
import Button from "../../shared/Button";

const StockpileView = ({}) => {
  const { id }: any = useParams();
  const dispatch = useAppDispatch();
  const stockpile = useAppSelector(getStockpile);

  useMemo(() => {
    if (id > 0) {
      storageAreaService.getStockpile(id).then((response) => {
        if (response.ok) {
          const stockpile = response.data as Stockpile;
          const setStockpileAction: SetStockpileAction = {
            stockpile,
          };
          dispatch(setStockpile(setStockpileAction));
        } else {
          toast.error(response.originalError.message);
        }
      });
    }
  }, []);

  const onSubmit = (values: StockpileFormValues, setSubmitting: Function) => {
    storageAreaService.saveStockpile(values as Stockpile).then((response) => {
      if (response.ok) {
        history.push(`/storage-areas`);
      } else {
        const data = response.data as string;
        toast.error(data);
        setSubmitting(false);
      }
    });

    setSubmitting(false);
  };

  const initialValues: StockpileFormValues = stockpile
    ? stockpile
    : {
        id: 0,
        name: "",
        status: "Active",
        capacity: 49,
        sequenceNumber: undefined,
      };

  // A closed stockpile is finalized (name is "Stockpile X Month D, YYYY") — lock
  // its number and capacity from edits.
  const isClosed = stockpile?.status === "Closed";

  // Physical stockpiles are numbered 1-10; the picked number drives the name.
  const STOCKPILE_NUMBERS = Array.from({ length: 10 }, (_, i) => i + 1);

  return (
    <Container maxWidth="xl">
      <PageHero
        title={id > 0 ? `${stockpile ? stockpile.name : id}` : "New Stockpile"}
        subtitle={id > 0 ? `Edit Stockpile.` : "Add Stockpile."}
        showBackButton={true}
      />
      <Formik
        initialValues={initialValues}
        validationSchema={StockpileFormSchema}
        onSubmit={(values, { setSubmitting }) =>
          onSubmit(values, setSubmitting)
        }
        enableReinitialize={true}
      >
        {({ values, handleSubmit, isSubmitting }) => (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                {isClosed ? (
                  <FormTextField
                    name="name"
                    label="Name"
                    value={values.name}
                    disabled={true}
                  />
                ) : (
                  <>
                    <FormSelect
                      name="sequenceNumber"
                      label="Stockpile Number"
                      value={values.sequenceNumber ?? null}
                      items={STOCKPILE_NUMBERS.map((n) => (
                        <MenuItem key={n} value={n}>
                          {n}
                        </MenuItem>
                      ))}
                    />
                    <FormErrorMessage name="sequenceNumber" />
                  </>
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                <FormSelect
                  name="status"
                  label="Status"
                  value={values.status}
                  items={STORAGE_AREA_STATUSES.map((status) => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                />
                <FormErrorMessage name="status" />
              </Grid>
              <Grid item xs={12} md={4}>
                <FormTextField
                  name="capacity"
                  type="number"
                  label="Capacity"
                  value={values.capacity}
                  disabled={isClosed}
                />
                <FormErrorMessage name="capacity" />
              </Grid>
              <Grid item xs={12} md={4}>
                <Button
                  label="Save"
                  color="primary"
                  variant="contained"
                  type="submit"
                  disabled={isSubmitting}
                />
              </Grid>
            </Grid>
          </form>
        )}
      </Formik>
    </Container>
  );
};

export default connect()(StockpileView);
